import type { ShopperBasketsTypes } from 'commerce-sdk-isomorphic'

export const buildCartList = (
  cart: ShopperBasketsTypes.Basket,
  wishlist: FlattenedWishlist,
  locale: Locales,
): CartDataLayer => {
  if (!cart || !cart.productItems) {
    return {
      products: [],
    }
  }

  const products: ProductDataLayer[] = cart.productItems.map((productItem, index) => {
    const item = mapProductLineItemToDataLayer(productItem, wishlist, locale)
    return {
      position: index + 1,
      ...item,
    }
  })

  return {
    creationDate: cart.creationDate,
    id: cart.basketId,
    lastModified: cart.lastModified,
    value: cart.orderTotal || (cart.shippingTotal ?? 0) + (cart.productTotal ?? 0),
    currency: cart.currency,
    products,
  }
}

interface AddOrRemoveBagProps {
  cartOpts: {
    product: Partial<Omit<FlattenedProduct, 'id'>>
    quantity: number
    currentCart: ShopperBasketsTypes.Basket
    customQuery?: {
      c_engravingText?: string
      c_ringSize?: string
      optionItems?: any
    }
  }
  gaOpts: {
    group: string
    location: string
    list?: string
  }
  locale: Locales
  wishlist: FlattenedWishlist
}

export const trackAddToBag = ({ cartOpts, locale, gaOpts, wishlist }: AddOrRemoveBagProps) => {
  const { product, currentCart, quantity, customQuery } = cartOpts
  const { group, location, list } = gaOpts

  pushEventToDataLayer(
    'custom.ecommerce.cart.add',
    {
      group,
      category: 'ecommerce/cart',
      label: product.name,
      info: {
        location,
      },
      data: undefined,
      ecommerceType: 'add',
      ecommerce: {
        currencyCode: currentCart.currency,
        add: {
          products: [
            mapProductToDataLayer(product, locale, {
              cartSource: location,
              engravingValue: customQuery?.c_engravingText || undefined,
              list,
              optionItems: customQuery?.optionItems || product.optionItems,
              quantity,
              selectedRingSize: customQuery?.c_ringSize || undefined,
              // TODO: product.representedProductId seems coming from nowhere, should be removed
              // wishlisted: wishlist?.some(
              //   (it) => it.productId === product.id || it.productId === product.representedProductId,
              // ),
              wishlisted: wishlist?.some((it) => it.productId === product.id),
            }),
          ],
        },
      },
      cart: {
        value: currentCart.orderTotal,
        products: currentCart.productItems?.map((product, index) => {
          const item = mapProductLineItemToDataLayer(product, wishlist, locale)
          return {
            position: index + 1,
            ...item,
          }
        }),
      },
    },
    locale,
  )
}

export const trackRemoveFromBag = ({ cartOpts, locale, gaOpts, wishlist }: AddOrRemoveBagProps) => {
  const { product, currentCart, quantity } = cartOpts
  const { group, location, list } = gaOpts
  const alignedProductToRemove = alignProductType(product)

  pushEventToDataLayer(
    'custom.ecommerce.cart.remove',
    {
      group,
      category: 'ecommerce/cart',
      label: product.name,
      info: {
        location,
      },
      data: undefined,
      ecommerceType: 'remove',
      ecommerce: {
        currencyCode: currentCart.currency,
        remove: {
          products: [
            mapProductToDataLayer(alignedProductToRemove, locale, {
              cartSource: location,
              engravingValue: product?.c_engravingText || undefined,
              list,
              optionItems: product.optionItems,
              quantity,
              selectedRingSize: product?.c_ringSize || undefined,
              wishlisted: wishlist?.some((it) => it.productId === product.id),
            }),
          ],
        },
      },
      cart: {
        value: currentCart.orderTotal,
        products: currentCart.productItems?.map((product, index) => {
          const item = mapProductLineItemToDataLayer(product, wishlist, locale)
          return {
            position: index + 1,
            ...item,
          }
        }),
      },
    },
    locale,
  )
}
